import * as Sentry from '@sentry/react';
import { AxiosError, isAxiosError } from 'axios';

import { PropsWithChildren } from 'react';

import { CONFIG } from '~src/config/config';
import { IgnoredError } from '~src/errors/IgnoredError';

Sentry.init({
  enabled: CONFIG.SENTRY_ENABLED,
  environment: CONFIG.SENTRY_ENV,
  dsn: CONFIG.SENTRY_DSN,
  integrations: [],
  /**
   * By returning null here, we can filter out handled errors so it doesn't clog Sentry.
   */
  beforeSend(event, hint) {
    const error = hint.originalException;

    if (error instanceof IgnoredError) {
      return null;
    }

    /**
     * Triggers when a native fetch call fails before reaching the server,
     * typically due to connectivity issues or the browser being offline.
     */
    const isFetchNetworkError = error instanceof TypeError && error.message === 'Failed to fetch';

    /**
     * Triggers when Axios surfaces a network-level failure (ERR_NETWORK),
     * meaning the request never received a response from the server.
     */
    const isAxiosNetworkError = isAxiosError(error) && error.code === AxiosError.ERR_NETWORK;

    /**
     * Client is offline, and fetch failed. This is the client connectivity issue we want to ignore.
     * We add this additional check to not accidentally exclude CORS-related issues.
     */
    if ((isFetchNetworkError || isAxiosNetworkError) && !navigator.onLine) {
      return null;
    }

    return event;
  },
});

export function LoggingProvider({ children }: PropsWithChildren) {
  return <Sentry.ErrorBoundary>{children}</Sentry.ErrorBoundary>;
}
